import type { Ref } from "react";
import { useT } from "../i18n/LocaleProvider";

interface Props {
  stepIndex: number;
  stepCount: number;
  prevDisabled: boolean;
  nextDisabled: boolean;
  duration: number;
  paused: boolean;
  pauseDisabled: boolean;
  /** "Still picture" mode is on (steps shown finished, no self-drawing motion). */
  still: boolean;
  onPrev: () => void;
  onNext: () => void;
  onSpeedChange: (duration: number) => void;
  onTogglePause: () => void;
  /** Switch between Animation and Still-picture mode. */
  onToggleStill: () => void;
  /** The play-button's progress-ring arc; the player drives its dash-offset from
   *  the drawing's live progress so it sweeps as the pen draws. */
  progressRef?: Ref<SVGCircleElement>;
}

// Speed slider maps a 0..100 position to how fast the pen moves (seconds to draw
// a reference length; the actual time per stroke scales with its length). Left =
// slow turtle, right = fast rabbit. We invert so dragging right speeds up.
const SLOW = 9;
const FAST = 1;
const toDuration = (pct: number) => SLOW - (pct / 100) * (SLOW - FAST);
const toPct = (duration: number) => ((SLOW - duration) / (SLOW - FAST)) * 100;

export function Controls({
  stepIndex,
  stepCount,
  prevDisabled,
  nextDisabled,
  duration,
  paused,
  pauseDisabled,
  still,
  onPrev,
  onNext,
  onSpeedChange,
  onTogglePause,
  onToggleStill,
  progressRef,
}: Props) {
  const t = useT();
  return (
    <div className="controls">
      <div className="nav-row">
        <button
          className="big-btn"
          onClick={onPrev}
          disabled={prevDisabled}
          aria-label={t.previousStep}
        >
          ◀
        </button>

        <div className="step-progress">
          {/* One dot per step — a round progress trail that reads nothing like
              the horizontal speed slider below. Done steps are filled, the
              current one pulses as it draws, upcoming ones are hollow. */}
          <div className="step-dots" aria-hidden="true">
            {Array.from({ length: stepCount }, (_, i) => {
              const state =
                i < stepIndex
                  ? "done"
                  : i === stepIndex
                    ? `current${paused ? " paused" : ""}`
                    : "";
              return <span key={i} className={`dot ${state}`.trimEnd()} />;
            })}
          </div>
          <div className="step-indicator" aria-live="polite">
            {t.stepOf(stepIndex + 1, stepCount)}
          </div>
        </div>

        <button
          className="big-btn"
          onClick={onNext}
          disabled={nextDisabled}
          aria-label={t.nextStep}
        >
          ▶
        </button>
      </div>

      <div className="pause-row">
        {/* The pause/play button ringed by a live progress arc that sweeps as
            the pen draws the current step (see the player's onProgress). A
            radial "bar" — unmistakable from the horizontal speed slider. */}
        <div className="pause-ring-wrap">
          <svg className="pause-ring" viewBox="0 0 46 46" aria-hidden="true">
            <circle className="pause-ring-track" cx="23" cy="23" r="20" />
            <circle
              ref={progressRef}
              className="pause-ring-fill"
              cx="23"
              cy="23"
              r="20"
              pathLength={1}
            />
          </svg>
          <button
            className={paused ? "pause-btn paused" : "pause-btn"}
            onClick={onTogglePause}
            disabled={pauseDisabled}
            aria-label={paused ? t.playDrawing : t.pauseDrawing}
            aria-pressed={paused}
          >
            {paused ? "▶️" : "⏸️"}
          </button>
        </div>

        {/* Mode toggle: watch it draw itself (Animation) vs. just see the
            finished picture at each step (Still). A chunky physical slide
            switch; the sliding knob carries the mode's icon. Persists. */}
        <button
          className={still ? "mode-switch on" : "mode-switch"}
          role="switch"
          aria-checked={still}
          onClick={onToggleStill}
          aria-label={still ? t.switchToAnimation : t.switchToStill}
        >
          <span className="mode-switch-text">
            {still ? t.modeStill : t.modeAnimate}
          </span>
          {/* Chevron points to the side the knob can slide toward, so it reads
              as a draggable switch. */}
          <span className="mode-switch-knob" aria-hidden="true">
            {still ? "❮" : "❯"}
          </span>
        </button>
      </div>

      <div className="speed-row">
        <span className="speed-icon" aria-hidden="true">
          🐢
        </span>
        <input
          type="range"
          min={0}
          max={100}
          value={toPct(duration)}
          onChange={(e) => onSpeedChange(toDuration(Number(e.target.value)))}
          aria-label={t.drawingSpeed}
        />
        <span className="speed-icon" aria-hidden="true">
          🐇
        </span>
      </div>

      <p className="hint-keys">
        {t.hintKeysBefore}
        <kbd>Space</kbd>
        {t.hintKeysMiddle}
        <kbd>←</kbd>
        {t.hintKeysAfter}
      </p>
    </div>
  );
}
